import { useMemo, useState } from 'react';

import { photoObjectPosition } from '../lib/members';

function normalizeToken(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-');
}

export default function MemberCard({ member, onOpenModal }) {
  const hasPhoto = Boolean(member?.photo);
  const [isLoaded, setIsLoaded] = useState(!hasPhoto);
  const [hasImageError, setHasImageError] = useState(false);
  const initials = useMemo(() => {
    const name = member?.name || member?.businessName || '';
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map(part => part[0])
      .join('')
      .toUpperCase();
  }, [member?.name, member?.businessName]);

  const chips = useMemo(() => {
    const role = normalizeToken(member?.role ?? member?.member_role ?? member?.memberRole);
    const status = normalizeToken(member?.status ?? member?.member_status ?? member?.memberStatus);
    const labels = [];

    if (role === 'executive') {
      labels.push({ label: 'Executive', className: 'bg-NBNA-primary text-white' });
    }
    if (role === 'guest' || role.includes('guest')) {
      labels.push({ label: 'Guest', className: 'bg-amber-100 text-amber-800' });
    }
    if (status === 'inactive' || status.includes('sub') || status.includes('subscriber') || status.includes('subscription')) {
      labels.push({ label: 'Sub', className: 'bg-slate-100 text-slate-700' });
    }

    return labels;
  }, [member]);

  return (
    <div 
      className={`
        bg-white rounded-lg shadow-md overflow-hidden transform transition-all duration-300
        hover:scale-[1.02] hover:shadow-lg cursor-pointer
        ${isLoaded ? 'animate-[slide-up_0.5s_ease-out]' : 'opacity-0'}
      `}
      onClick={() => onOpenModal?.(member)}
    >
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden">
        {hasPhoto && !hasImageError ? (
          <img
            src={member.photo}
            alt={member.name}
            onLoad={() => setIsLoaded(true)}
            onError={() => {
              setHasImageError(true);
              setIsLoaded(true);
            }}
            style={{ objectPosition: photoObjectPosition(member) }}
            className="w-full h-full object-cover filter grayscale transition-transform duration-300 hover:scale-105"
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center bg-NBNA-primary/10 text-3xl font-semibold text-NBNA-primary"
            aria-label={`${member?.name || 'Member'} avatar placeholder`}
          >
            {initials || 'MB'}
          </div>
        )}
        {chips.length > 0 ? (
          <div className="absolute right-2 top-2 flex flex-col items-end gap-1">
            {chips.map((chip) => (
              <div
                key={chip.label}
                className={`rounded-full px-2 py-1 text-xs ${chip.className}`}
              >
                {chip.label}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-playfair-sans text-NBNA-primary mb-1">
          {member.businessName}
        </h3>
        <p className="text-sm text-gray-600 mb-2">
          {member.name}
        </p>
        <p className="text-sm text-gray-700 line-clamp-2">
          {member.shortDescription}
        </p>
      </div>
    </div>
  );
}
